import React from 'react';
import { useNavigation } from '@react-navigation/native';
import { useUserContext } from '../../../common/utils/context';
import { isMyDayFeatureEnabledForTenant, isMyDayUserSettingEnabled } from '../../../common/utils/myDay/access';
import { useTheme } from '../../theme';
import { isLegacyMobileUi } from '../../utils/legacyMobileUi';
import { THEME_MODE_OPTIONS } from '../../design/tokens';
import { Screen } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

import {
	Button,
	Picker,
	SettingsRow,
	SettingsSection,
	SettingsSwitchRow,
} from '../../components';
import { confirmStockLevelsSortOptions, defaultConfirmStockLevelsSort, pickListSummarySortOptions } from '../../utils/sortMethods';
import { getProductHealthComplianceLocalLabel } from '../../../common/utils/products/healthCompliance';
import { DATE_PICKER_STYLE, DATE_PICKER_STYLE_OPTIONS } from '../../utils/datePickerConstants';
import {
	DEFAULT_SENTRY_PUSH_NOTIFICATION_TYPES,
	PREDEFINED_USER_ROLES,
	SENTRY_PUSH_NOTIFICATION_TYPE_NAMES,
	SENTRY_PUSH_NOTIFICATION_TYPES,
} from '../../../common/constants';

const pickListSummaryModeOptions = [
	{ label: 'List', value: 'list' },
	{ label: 'Grid', value: 'grid' },
];

const APP_RUNTIME = {
	IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
};

const sentryPushNotificationOptions = Object.values(SENTRY_PUSH_NOTIFICATION_TYPES).map(value => ({
	value,
	label: SENTRY_PUSH_NOTIFICATION_TYPE_NAMES[value],
}));

const Settings = () => {

	const {
		user,
		modifyUserSettings,
		tenant,
		tenantSettings,
		isDriver,
		isPicker,
		isAVC,
	} = useUserContext();
	const { planogramItemDaysSinceLastSale } = tenantSettings;
	const { mode, setMode } = useTheme();
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const canAccessMyDayFeature = isMyDayFeatureEnabledForTenant({
		tenantSettings,
		isAVC,
		isDevelopment: APP_RUNTIME.IS_DEVELOPMENT,
	});
	const isAVCAdmin = Boolean(isAVC && (
		user?.isSuperAdmin || user?.role?.name === PREDEFINED_USER_ROLES.ADMIN
	));
	// Non-driver/picker staff (e.g. admins covering runs) can have My day enabled
	// too; showing the toggle to them keeps a self-service way back to classic.
	// The switch only means something on the legacy screens; the redesigned
	// driver flow ignores it.
	const canUseMyDay = Boolean(isLegacyMobileUi(user) && canAccessMyDayFeature && (
		isDriver || isPicker || isAVCAdmin || isMyDayUserSettingEnabled(user)
	));

	const {
		settings: {
			pickListSort,
			picklistShowAllSelections,
			picklistBatchSameProduct,
			picklistHideImages,
			picklistHideHealthCompliance,
			picklistHidePickedItems,
			picklistShowSalesLastXDays,
			picklistShowQuantitySoldAfterRecalc,
			picklistShowExpiryDates,
			confirmStockLevelsSort,
			confirmStockLevelsHideImages,
			confirmStockLevelsHideHealthCompliance,
			confirmStockLevelsShowExpiryDateButtons,
			confirmStockLevelsShowSalesLastXDays,
			picklistShowPickOrder,
			pickListSummaryHideImages,
			pickListSummaryShowProductName,
			pickListSummarySort,
			pickListSummaryMode,
			pickListSummarySelectAllMachines,
			pickListSummaryShowAllSelections,
			pickListSummaryShowExpiryWarnings,
			datePickerStyle,
			enabledPushNotifications,
		},
	} = user;
	const enabledPushNotificationTypes = Array.isArray(enabledPushNotifications)
		? enabledPushNotifications.map(Number)
		: DEFAULT_SENTRY_PUSH_NOTIFICATION_TYPES;

	const setPickListSort = value => modifyUserSettings({ pickListSort: value });
	const toggleShowAllSelections = value => modifyUserSettings({ picklistShowAllSelections: value });
	const toggleBatchSameProduct = value => modifyUserSettings({ picklistBatchSameProduct: value });
	const toggleHideImages = value => modifyUserSettings({ picklistHideImages: value });
	const togglePicklistHideHealthCompliance = value => modifyUserSettings({ picklistHideHealthCompliance: value });
	const toggleHidePickedItems = value => modifyUserSettings({ picklistHidePickedItems: value });
	const toggleShowSalesLastXDaysForPicklists = value => modifyUserSettings({ picklistShowSalesLastXDays: value });
	const toggleShowPickOrderForPicklists = value => modifyUserSettings({ picklistShowPickOrder: value });
	const toggleShowQuantitySoldAfterRecalc = value => modifyUserSettings({ picklistShowQuantitySoldAfterRecalc: value });
	const toggleShowExpiryDates = value => modifyUserSettings({ picklistShowExpiryDates: value });

	const setConfirmStockLevelsSort = value => modifyUserSettings({ confirmStockLevelsSort: value });
	const toggleCnfirmStockLevelsHideImages = value => modifyUserSettings({ confirmStockLevelsHideImages: value });
	const toggleConfirmStockLevelsHideHealthCompliance = value => modifyUserSettings({ confirmStockLevelsHideHealthCompliance: value });
	const toggleConfirmStockLevelsShowExpiryDateButtons = value => modifyUserSettings({ confirmStockLevelsShowExpiryDateButtons: value });
	const toggleConfirmStockLevelsShowSalesLastXDays = value => modifyUserSettings({ confirmStockLevelsShowSalesLastXDays: value });
	const setPickListSummaryMode = value => modifyUserSettings({ pickListSummaryMode: value });
	const togglePickListSummaryHideImages = value => modifyUserSettings({ pickListSummaryHideImages: value });
	const togglePickListSummaryShowProductName = value => modifyUserSettings({ pickListSummaryShowProductName: value });
	const setPickListSummarySort = value => modifyUserSettings({ pickListSummarySort: value });
	const togglePickListSummarySelectAllMachines = value => modifyUserSettings({ pickListSummarySelectAllMachines: value });
	const togglePickListSummaryShowAllSelections = value => modifyUserSettings({ pickListSummaryShowAllSelections: value });
	const togglePickListSummaryShowExpiryWarnings = value => modifyUserSettings({ pickListSummaryShowExpiryWarnings: value });

	const setDatePickerStyle = value => modifyUserSettings({ datePickerStyle: value });
	const toggleMyDayEnabled = value => modifyUserSettings({
		myDayEnabled: value,
		preferClassicHome: !value,
	});
	const setPushNotificationEnabled = type => value => {
		const nextEnabledPushNotifications = value
			? Array.from(new Set([...enabledPushNotificationTypes, type]))
			: enabledPushNotificationTypes.filter(enabledType => enabledType !== type);

		modifyUserSettings({ enabledPushNotifications: nextEnabledPushNotifications });
	};

	return (
		<Screen
			heading="Settings"
			subheading="User settings"
			contentContainerStyle={styles.content}
		>
			<SettingsSection title="General settings">
				<SettingsRow label="Theme mode">
					<Picker
						selectedValue={mode}
						onValueChange={setMode}
						options={THEME_MODE_OPTIONS}
						width={190}
					/>
				</SettingsRow>
				<SettingsRow label="Date picker style">
					<Picker
						selectedValue={datePickerStyle || DATE_PICKER_STYLE.STEPPED}
						onValueChange={setDatePickerStyle}
						options={DATE_PICKER_STYLE_OPTIONS}
						width={190}
					/>
				</SettingsRow>
				{
					canUseMyDay && (
						<SettingsSwitchRow
							onValueChange={toggleMyDayEnabled}
							value={isMyDayUserSettingEnabled(user)}
							label="Use My day"
						/>
					)
				}
			</SettingsSection>

			{
				// Dev builds only: the component gallery the redesign reviewers use.
				// eslint-disable-next-line no-undef
				__DEV__ && (
					<SettingsSection title="Developer">
						<SettingsRow label="Design gallery" description="Every redesign component in all three themes">
							<Button label="Open" onPress={() => navigation.navigate('DesignGalleryView')} />
						</SettingsRow>
					</SettingsSection>
				)
			}

			<SettingsSection title="Push notifications">
				{
					sentryPushNotificationOptions.map(option => (
						<SettingsSwitchRow
							key={option.value}
							onValueChange={setPushNotificationEnabled(option.value)}
							value={enabledPushNotificationTypes.includes(option.value)}
							label={option.label}
						/>
					))
				}
			</SettingsSection>

			<SettingsSection title="Pick list summary defaults">
				<SettingsRow label="View mode">
					<Picker
						selectedValue={pickListSummaryMode || 'list'}
						onValueChange={setPickListSummaryMode}
						options={pickListSummaryModeOptions}
						width={190}
					/>
				</SettingsRow>
				<SettingsRow label="Sort by">
					<Picker
						selectedValue={pickListSummarySort || 'quantityDsc'}
						onValueChange={setPickListSummarySort}
						options={pickListSummarySortOptions}
						width={190}
					/>
				</SettingsRow>
				<SettingsSwitchRow
					onValueChange={togglePickListSummaryShowAllSelections}
					value={pickListSummaryShowAllSelections}
					label="Show all selections"
				/>
				{
					tenantSettings.useExpiryDates && (
						<SettingsSwitchRow
							onValueChange={togglePickListSummaryShowExpiryWarnings}
							value={pickListSummaryShowExpiryWarnings}
							label="Show expiry warnings"
						/>
					)
				}
				<SettingsSwitchRow
					onValueChange={togglePickListSummaryHideImages}
					value={pickListSummaryHideImages}
					label="Hide images in list view"
				/>
				<SettingsSwitchRow
					onValueChange={togglePickListSummaryShowProductName}
					value={pickListSummaryShowProductName}
					label="Show product names in grid view"
				/>
				<SettingsSwitchRow
					onValueChange={togglePickListSummarySelectAllMachines}
					value={pickListSummarySelectAllMachines}
					label="Automatically select all POS in grid view"
				/>
			</SettingsSection>

			<SettingsSection title="Pick list defaults">
				<SettingsRow label="Sort by">
					<Picker
						selectedValue={pickListSort || 'pickOrderAsc'}
						onValueChange={setPickListSort}
						options={pickListSummarySortOptions}
						width={190}
					/>
				</SettingsRow>
				<SettingsSwitchRow
					onValueChange={toggleShowAllSelections}
					value={picklistShowAllSelections}
					label="Show all selections"
				/>
				<SettingsSwitchRow
					onValueChange={toggleBatchSameProduct}
					value={picklistBatchSameProduct}
					label="Batch same product"
				/>
				<SettingsSwitchRow
					onValueChange={toggleHideImages}
					value={picklistHideImages}
					label="Hide images"
				/>
				<SettingsSwitchRow
					onValueChange={togglePicklistHideHealthCompliance}
					value={picklistHideHealthCompliance}
					label={`Hide ${getProductHealthComplianceLocalLabel(tenant, true)} category`}
				/>
				<SettingsSwitchRow
					onValueChange={toggleHidePickedItems}
					value={picklistHidePickedItems}
					label="Hide picked items"
				/>
				<SettingsSwitchRow
					onValueChange={toggleShowSalesLastXDaysForPicklists}
					value={picklistShowSalesLastXDays}
					label={`Show sales last ${planogramItemDaysSinceLastSale} days`}
				/>
				<SettingsSwitchRow
					onValueChange={toggleShowPickOrderForPicklists}
					value={picklistShowPickOrder}
					label="Show pick order"
				/>
				<SettingsSwitchRow
					onValueChange={toggleShowQuantitySoldAfterRecalc}
					value={picklistShowQuantitySoldAfterRecalc}
					label="Show quantity sold after recalculation"
				/>
				{
					tenantSettings.useExpiryDates && (
						<SettingsSwitchRow
							onValueChange={toggleShowExpiryDates}
							value={picklistShowExpiryDates}
							label="Show expiry dates"
						/>
					)
				}
			</SettingsSection>

			<SettingsSection title="Confirm stock level defaults">
				<SettingsRow label="Sort by">
					<Picker
						selectedValue={confirmStockLevelsSort || defaultConfirmStockLevelsSort}
						onValueChange={setConfirmStockLevelsSort}
						options={confirmStockLevelsSortOptions}
						width={190}
					/>
				</SettingsRow>
				<SettingsSwitchRow
					onValueChange={toggleCnfirmStockLevelsHideImages}
					value={confirmStockLevelsHideImages}
					label="Hide images"
				/>
				<SettingsSwitchRow
					onValueChange={toggleConfirmStockLevelsHideHealthCompliance}
					value={confirmStockLevelsHideHealthCompliance}
					label={`Hide ${getProductHealthComplianceLocalLabel(tenant, true)} category`}
				/>
				<SettingsSwitchRow
					onValueChange={toggleConfirmStockLevelsShowExpiryDateButtons}
					value={confirmStockLevelsShowExpiryDateButtons}
					label="Show expiry date buttons"
				/>
				<SettingsSwitchRow
					onValueChange={toggleConfirmStockLevelsShowSalesLastXDays}
					value={confirmStockLevelsShowSalesLastXDays}
					label={`Show sales last ${planogramItemDaysSinceLastSale} days`}
				/>
			</SettingsSection>
		</Screen>
	);
};

// Sections bring their own top margin.
const createStyles = () => ({
	content: {
		gap: 0,
	},
});

export default Settings;
